<template>
  <div class="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
    <div class="max-w-md w-full space-y-8">
      <div class="text-center">
        <div
          class="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 mb-4"
        >
          <svg class="h-6 w-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
            />
          </svg>
        </div>

        <h2 class="mt-6 text-3xl font-extrabold text-gray-900">
          {{ pageTitle }}
        </h2>
        <p class="mt-2 text-sm text-gray-600">
          {{ pageDescription }}
        </p>
      </div>

      <!-- Loading State -->
      <div v-if="verificationState === 'verifying'" class="text-center">
        <LoadingSpinner size="lg" />
        <p class="mt-4 text-sm text-gray-600">Verifying your email address...</p>
      </div>

      <!-- Success State -->
      <div v-else-if="verificationState === 'success'" class="text-center space-y-4">
        <div class="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
          <svg class="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M5 13l4 4L19 7"
            />
          </svg>
        </div>
        <Alert
          type="success"
          title="Email verified successfully!"
          message="Your email address has been verified. You can now access all features."
        />
        <Button variant="primary" size="lg" class="w-full" @click="redirectToDashboard">
          {{ isAuthenticated ? 'Continue to Dashboard' : 'Sign in' }}
        </Button>
      </div>

      <!-- Error State -->
      <div v-else-if="verificationState === 'error'" class="text-center space-y-4">
        <div class="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
          <svg class="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </div>
        <Alert type="error" title="Verification failed" :message="errorMessage" />

        <!-- Success message for resend -->
        <Alert
          v-if="resendSuccess"
          type="success"
          title="Verification email sent!"
          message="Please check your email for a new verification link."
        />

        <div class="space-y-2">
          <Button
            variant="outline"
            size="lg"
            class="w-full"
            @click="resendVerification"
            :loading="isResending"
            :disabled="resendSuccess"
          >
            {{
              resendSuccess
                ? 'Email sent!'
                : isResending
                  ? 'Sending...'
                  : 'Resend verification email'
            }}
          </Button>
          <Button variant="ghost" size="lg" class="w-full" @click="$router.push('/auth/login')">
            Back to login
          </Button>
        </div>
      </div>

      <!-- No Token State -->
      <div v-else-if="verificationState === 'no-token'" class="text-center space-y-4">
        <EmailVerificationPrompt
          v-if="userEmail"
          :email="userEmail"
          @error="handlePromptError"
          @resend="handleResend"
        />
        <div v-else class="text-center space-y-4">
          <div
            class="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100"
          >
            <svg
              class="h-6 w-6 text-yellow-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
          </div>
          <h3 class="text-lg font-medium text-gray-900">Email address required</h3>
          <p class="text-gray-600">
            Please provide your email address to send a verification link.
          </p>
          <Button variant="primary" size="lg" class="w-full" @click="$router.push('/auth/login')">
            Back to login
          </Button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { ref, computed, onMounted, watch } from 'vue';
  import { useRoute, useRouter } from 'vue-router';
  import { Button, Alert, LoadingSpinner } from '@asabdev/asab-ui';
  import { useAuth } from '@/features/auth/composables/useAuth';
  import { EmailVerificationPrompt } from '@/features/auth/components';
  import { getErrorMessage, isAuthError, AUTH_ERROR_CODES } from '@/lib/api/errors';
  import { useAuthStore } from '@/stores/auth';
  import type { AxiosError } from 'axios';

  type VerificationState = 'verifying' | 'success' | 'error' | 'no-token';

  const route = useRoute();
  const router = useRouter();
  const { isAuthenticated } = useAuth();
  const authStore = useAuthStore();

  // Initialize state based on whether a token is present in the URL
  const verificationState = ref<VerificationState>(route.query.token ? 'verifying' : 'no-token');
  const errorMessage = ref('');
  const isResending = ref(false);
  const resendSuccess = ref(false);
  const lastResendTime = ref(0);

  // Get token from query params
  const token = computed(() => (route.query.token as string) || '');

  // Watch for token changes to handle navigation between verification links
  watch(token, newToken => {
    if (newToken) {
      verifyEmail(newToken);
    } else if (verificationState.value === 'verifying') {
      verificationState.value = 'no-token';
    }
  });

  // Get user email for verification prompt
  const userEmail = computed(() => (route.query.email as string) || authStore.user?.email || '');

  const pageTitle = computed(() => {
    switch (verificationState.value) {
      case 'verifying':
        return 'Verifying your email';
      case 'success':
        return 'Email verified!';
      case 'error':
        return 'Verification failed';
      case 'no-token':
        return 'Check your email';
      default:
        return 'Verify your email';
    }
  });

  const pageDescription = computed(() => {
    switch (verificationState.value) {
      case 'verifying':
        return 'Please wait while we verify your email address...';
      case 'success':
        return 'Your email has been successfully verified.';
      case 'error':
        return "We couldn't verify your email address.";
      case 'no-token':
        return "We've sent a verification link to your email address.";
      default:
        return 'Please check your email for a verification link.';
    }
  });

  const verifyEmail = async (verificationToken: string) => {
    try {
      verificationState.value = 'verifying';
      await authStore.verifyEmail(verificationToken);
      verificationState.value = 'success';

      // After successful verification, redirect to dashboard after a short delay
      setTimeout(() => {
        router.push('/app');
      }, 2000); // 2 second delay to show success message
    } catch (error: unknown) {
      verificationState.value = 'error';
      const axiosError = error as AxiosError;

      // Per-status disabled mapping. FE owns the copy; getErrorMessage
      // looks the code up in canned ERROR_MESSAGES — never surfaces BE prose.
      if (
        isAuthError(axiosError, AUTH_ERROR_CODES.LOGIN_ACCOUNT_BANNED) ||
        isAuthError(axiosError, AUTH_ERROR_CODES.LOGIN_ACCOUNT_SUSPENDED) ||
        isAuthError(axiosError, AUTH_ERROR_CODES.LOGIN_ACCOUNT_DEACTIVATED) ||
        isAuthError(axiosError, AUTH_ERROR_CODES.LOGIN_ACCOUNT_DISABLED)
      ) {
        errorMessage.value = getErrorMessage(
          axiosError,
          'Email verification is not available for this account.',
        );
        return;
      }

      if (isAuthError(axiosError, AUTH_ERROR_CODES.VERIFY_USER_BAD_TOKEN)) {
        errorMessage.value =
          'This verification link has expired or is invalid. Please request a new one.';
      } else if (isAuthError(axiosError, AUTH_ERROR_CODES.VERIFY_USER_ALREADY_VERIFIED)) {
        errorMessage.value = 'Your email address is already verified. You can now sign in.';
        // Redirect to login after showing success
        setTimeout(() => {
          router.push('/auth/login');
        }, 2000);
      } else {
        errorMessage.value = getErrorMessage(
          axiosError,
          'Email verification failed. The link may be expired or invalid.',
        );
      }
    }
  };

  const resendVerification = async (emailOrEvent?: string | MouseEvent) => {
    // Prevent duplicate calls
    if (isResending.value) {
      return;
    }

    // Debounce mechanism - prevent multiple calls within 2 seconds
    const now = Date.now();
    if (now - lastResendTime.value < 2000) {
      return;
    }
    lastResendTime.value = now;

    try {
      isResending.value = true;
      resendSuccess.value = false;

      // If it's a MouseEvent or undefined, use the userEmail.value
      // If it's a string, use it as the email
      const email = typeof emailOrEvent === 'string' ? emailOrEvent : userEmail.value;

      if (!email) {
        errorMessage.value = 'Email address is required to resend verification.';
        return;
      }

      await authStore.requestVerifyToken(email);

      // Success - clear error and show success message
      errorMessage.value = '';
      resendSuccess.value = true;

      // Clear success message after 5 seconds
      setTimeout(() => {
        resendSuccess.value = false;
      }, 5000);
    } catch (error: unknown) {
      const axiosError = error as AxiosError;
      resendSuccess.value = false;

      // Use proper error handling for user-friendly messages
      if (axiosError.response?.status === 429) {
        errorMessage.value = 'Too many requests. Please wait a moment before trying again.';
      } else if (axiosError.response?.status === 404) {
        errorMessage.value = 'No account found with this email address.';
      } else {
        errorMessage.value = getErrorMessage(
          axiosError,
          'Failed to resend verification email. Please try again.'
        );
      }
    } finally {
      isResending.value = false;
    }
  };

  const handlePromptError = (error: string) => {
    errorMessage.value = error;
  };

  const handleResend = async (email: string) => {
    // Pass the email directly to the resendVerification function
    await resendVerification(email);
  };

  const redirectToDashboard = () => {
    router.push('/app');
  };

  onMounted(() => {
    // If authenticated and verified, redirect to dashboard
    if (isAuthenticated.value && authStore.isVerified) {
      router.push('/app');
      return;
    }

    // If we have a token, verify it
    if (token.value) {
      verifyEmail(token.value);
    } else {
      // No token, show verification prompt
      verificationState.value = 'no-token';

      // If we came from the login page with an email in the query params,
      // make sure it's displayed in the verification prompt
      if (route.query.email && typeof route.query.email === 'string') {
        console.log('Email from query params:', route.query.email);
      }
    }
  });
</script>
